import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';

const VerifyEmailPage = () => {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await verifyEmail(email, code);
      setSuccess('Email verificado correctamente. Redirigiendo...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Por favor ingresa tu email');
      return;
    }
    setResending(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification?email=${encodeURIComponent(email)}`, {
        method: 'POST'
      });
      if (response.ok) {
        setSuccess('Código reenviado. Revisa tu email.');
      } else {
        throw new Error('Error al reenviar');
      }
    } catch (err) {
      setError('Error al reenviar el código');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-zinc-950">
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800 backdrop-blur-sm">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-violet-500/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-violet-400" />
          </div>
          <CardTitle className="text-2xl font-bold">Verifica tu Email</CardTitle>
          <CardDescription className="text-zinc-400">
            Ingresa el código de 6 dígitos enviado a tu correo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                {success}
              </div>
            )}

            <div className="space-y-2">
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-950 border-zinc-800 focus:border-violet-500"
                data-testid="verify-email-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Input
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-zinc-950 border-zinc-800 focus:border-violet-500 text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                data-testid="verify-code-input"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-violet-600 hover:bg-violet-700"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Verificar Email'
              )}
            </Button>

            <div className="text-center space-y-2">
              <Button
                type="button"
                variant="ghost"
                className="text-violet-400 hover:text-violet-300"
                onClick={handleResend}
                disabled={resending}
                data-testid="resend-code-btn"
              >
                {resending ? 'Reenviando...' : '¿No recibiste el código? Reenviar'}
              </Button>
              
              <p className="text-sm text-zinc-500">
                <Link to="/login" className="text-violet-400 hover:text-violet-300">
                  Volver al login
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
